import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarCheck, ChevronRight, ClipboardList, MessageCircle, Plus, Trash2, UserX, Users, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { DatePicker } from '../../components/ui/date-picker';
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { formatDate } from '../../lib/utils';
import { getModuleConfig, type ModuleKey } from '../../lib/moduleConfig';
import PaymentSummary from '../../components/PaymentSummary';

const CLASS_NAMES: Record<string, string> = {
  '1': 'Class 6',
  '2': 'Class 7',
  '3': 'Class 8',
  '4': 'Class 9',
  '5': 'Class 10',
  '6': 'Class 11',
  '7': 'Class 12',
};

export default function ModuleClassHubPage() {
  const { module, classId } = useParams();
  const config = getModuleConfig(module);
  const moduleKey = (module || 'students') as ModuleKey;
  const displayClassName = CLASS_NAMES[classId || ''] || `Class ${classId}`;

  const { data: batches } = useQuery({
    queryKey: ['module-batches', classId],
    queryFn: async () => (await api.get<any>(`/api/batches?classId=${classId}`)).data || [],
    enabled: !!classId,
  });

  const activeBatches = (batches || []).filter((b: any) => b.status !== 'inactive' && b.status !== 'completed');

  // Only students module shows Dropped/Enquiry cards
  const showStatusCards = moduleKey === 'students';

  return (
    <div className="space-y-4 pb-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon">
          <Link to={`/modules/${moduleKey}/classes`}><ArrowLeft className="h-5 w-5" /></Link>
        </Button>
        <div>
          <p className="text-xs text-light-text-muted">{config.label}</p>
          <h1 className="text-xl font-black text-light-text dark:text-dark-text">{displayClassName}</h1>
        </div>
      </div>

      {moduleKey === 'attendance' && (
        <Link
          to={`/attendance?classId=${classId}`}
          className="rounded-[1.5rem] bg-lime-50 dark:bg-lime-500/10 border border-lime-200 dark:border-lime-500/20 p-4 flex items-center justify-between active:scale-[0.99]"
        >
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-lime-500 text-white flex items-center justify-center">
              <CalendarCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="font-black text-lime-900 dark:text-lime-100">Mark Attendance</p>
              <p className="text-xs text-lime-700/75 dark:text-lime-200/70">Record today's attendance for this class</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-lime-700" />
        </Link>
      )}

      {moduleKey === 'payments' && (
        <div className="rounded-[1.5rem] bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 p-4">
          <h2 className="text-sm font-bold text-amber-900 dark:text-amber-100 mb-3 flex items-center gap-2">
            <IndianRupee className="w-4 h-4" />
            {displayClassName} - Payment Summary
          </h2>
          <PaymentSummary classId={classId} showClassBreakdown={false} />
        </div>
      )}

      {moduleKey === 'exams' ? (
        <ExamsClassSection classId={classId!} moduleKey={moduleKey} />
      ) : (
        <div className="rounded-[1.5rem] bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-5 w-5 text-emerald-600" />
            <h2 className="font-bold text-light-text dark:text-dark-text">Running batches</h2>
          </div>
          <div className="space-y-2">
            {/* Total - All Batches */}
            <Link
              to={`/modules/${moduleKey}/classes/${classId}/batches/total`}
              className="flex items-center justify-between rounded-2xl bg-purple-50 dark:bg-purple-500/10 border border-purple-100 dark:border-purple-500/20 p-3 active:scale-[0.99]"
            >
              <div>
                <p className="font-bold text-purple-900 dark:text-purple-100">Total</p>
                <p className="text-xs text-purple-700/75 dark:text-purple-200/70">All students across all batches</p>
              </div>
              <ChevronRight className="h-5 w-5 text-purple-700" />
            </Link>
            {activeBatches.length ? activeBatches.map((batch: any) => (
              <Link
                key={batch.id}
                to={`/modules/${moduleKey}/classes/${classId}/batches/${batch.id}`}
                className="flex items-center justify-between rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 p-3 active:scale-[0.99]"
              >
                <div>
                  <p className="font-bold text-emerald-900 dark:text-emerald-100">{batch.name}</p>
                  <p className="text-xs text-emerald-700/75 dark:text-emerald-200/70">Active batch</p>
                </div>
                <ChevronRight className="h-5 w-5 text-emerald-700" />
              </Link>
            )) : (
              <div className="rounded-2xl bg-light-elevated dark:bg-dark-elevated p-4 text-sm text-light-text-muted">No running batches for this class yet.</div>
            )}
          </div>
        </div>
      )}

      {showStatusCards && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Link
          to={`/modules/${moduleKey}/classes/${classId}/status/drop`}
          className="rounded-[1.5rem] bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 p-4 flex items-center justify-between active:scale-[0.99]"
        >
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-rose-500 text-white flex items-center justify-center"><UserX className="h-5 w-5" /></div>
            <div>
              <p className="font-black text-rose-900 dark:text-rose-100">Dropped</p>
              <p className="text-xs text-rose-700/75 dark:text-rose-200/70">Students who left this class</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-rose-700" />
        </Link>

        <Link
          to={`/modules/${moduleKey}/classes/${classId}/enquiries`}
          className="rounded-[1.5rem] bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20 p-4 flex items-center justify-between active:scale-[0.99]"
        >
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-amber-500 text-white flex items-center justify-center"><MessageCircle className="h-5 w-5" /></div>
            <div>
              <p className="font-black text-amber-900 dark:text-amber-100">Enquiries</p>
              <p className="text-xs text-amber-700/75 dark:text-amber-200/70">Students asking for admission</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-amber-700" />
        </Link>
      </div>
      )}
    </div>
  );
}

function ExamsClassSection({ classId, moduleKey }: { classId: string; moduleKey: string }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [examForm, setExamForm] = useState({ name: '', examDate: '', totalMarks: '', batchId: '' });

  const queryKey = ['module-exams', classId];
  const { data: exams, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get<any>(`/api/exams?classId=${classId}`)).data || [],
  });

  const { data: batches } = useQuery({
    queryKey: ['module-batches', classId],
    queryFn: async () => (await api.get<any>(`/api/batches?classId=${classId}`)).data || [],
    enabled: !!classId,
  });

  const createExam = useMutation({
    mutationFn: async () => api.post('/api/exams', {
      name: examForm.name,
      classId: Number(classId),
      batchId: examForm.batchId ? Number(examForm.batchId) : undefined,
      examDate: examForm.examDate || undefined,
      totalMarks: Number(examForm.totalMarks),
    }),
    onSuccess: () => {
      toast.success('Exam created');
      setShowForm(false);
      setExamForm({ name: '', examDate: '', totalMarks: '', batchId: '' });
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to create exam'),
  });

  const deleteExam = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/exams/${id}`),
    onSuccess: () => { toast.success('Exam deleted'); queryClient.invalidateQueries({ queryKey }); },
  });

  return (
    <div className="space-y-3">
      <div className="rounded-[1.5rem] bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-rose-600" />
            <h2 className="font-bold text-light-text dark:text-dark-text">Exams</h2>
          </div>
          <Button size="sm" onClick={() => setShowForm(true)}><Plus className="w-4 h-4" /> Add Exam</Button>
        </div>

        {showForm && (
          <Card className="mb-3">
            <CardHeader><CardTitle className="text-base">New Exam</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Input placeholder="Exam name" value={examForm.name} onChange={(e) => setExamForm({ ...examForm, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <DatePicker value={examForm.examDate} onChange={(v) => setExamForm({ ...examForm, examDate: v })} placeholder="Date" />
                <Input type="number" placeholder="Total marks" value={examForm.totalMarks} onChange={(e) => setExamForm({ ...examForm, totalMarks: e.target.value })} />
              </div>
              <Select value={examForm.batchId || 'all'} onValueChange={(v) => setExamForm({ ...examForm, batchId: v === 'all' ? '' : v })}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="All batches" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Batches</SelectItem>
                  {(batches || []).map((b: any) => (<SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => createExam.mutate()} disabled={!examForm.name || !examForm.totalMarks}>Create</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-2">
          {isLoading ? (
            [1, 2, 3].map(i => <div key={i} className="h-14 rounded-2xl bg-light-elevated dark:bg-dark-elevated animate-pulse" />)
          ) : exams?.length ? exams.map((exam: any) => (
            <Link
              key={exam.id}
              to={`/modules/${moduleKey}/classes/${classId}/exams/${exam.id}`}
              className="flex items-center justify-between rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 p-3 active:scale-[0.99]"
            >
              <div className="min-w-0">
                <p className="font-bold text-rose-900 dark:text-rose-100 truncate">{exam.name}</p>
                <p className="text-xs text-rose-700/75 dark:text-rose-200/70">
                  {exam.total_marks} marks
                  {exam.exam_date ? ` · ${formatDate(exam.exam_date)}` : ''}
                  {exam.result_count != null ? ` · ${exam.result_count} students` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:bg-rose-100 dark:hover:bg-rose-500/20" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteConfirmId(exam.id); }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
                <ChevronRight className="h-5 w-5 text-rose-700" />
              </div>
            </Link>
          )) : (
            <div className="rounded-2xl bg-light-elevated dark:bg-dark-elevated p-4 text-sm text-light-text-muted">No exams for this class yet.</div>
          )}
        </div>
      </div>

      {/* Also show batches for per-student exam view */}
      <div className="rounded-[1.5rem] bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-5 w-5 text-emerald-600" />
          <h2 className="font-bold text-light-text dark:text-dark-text">Student Exam Records</h2>
        </div>
        <div className="space-y-2">
          {(batches || []).filter((b: any) => b.status !== 'inactive').map((batch: any) => (
            <Link
              key={batch.id}
              to={`/modules/${moduleKey}/classes/${classId}/batches/${batch.id}`}
              className="flex items-center justify-between rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 p-3 active:scale-[0.99]"
            >
              <div>
                <p className="font-bold text-emerald-900 dark:text-emerald-100">{batch.name}</p>
                <p className="text-xs text-emerald-700/75 dark:text-emerald-200/70">View per-student exam history</p>
              </div>
              <ChevronRight className="h-5 w-5 text-emerald-700" />
            </Link>
          ))}
        </div>
      </div>

      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Exam</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this exam? All results will be lost.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-500 hover:bg-rose-600 text-white" onClick={() => deleteConfirmId && deleteExam.mutate(deleteConfirmId)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
